import { useCallback, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type {
  AiAllergy,
  GenerateDraftResponse as ContractGenerateDraftResponse,
} from '@behuman/core-contract'
import { useCallSession } from '@/components/session'
import { useClient } from '@/hooks/useClient'
import { logError, logWarning } from '@/utils/errorUtils'
import {
  isDraftAlreadyGenerating,
  isEmptyTranscriptError,
  isStaleScribeSession404,
  ScribeDraftGenerationError,
} from './scribeErrors'

// All draft types derive from the zod contract (`@behuman/core-contract`) —
// the scribe endpoints moved to oRPC, so the generated OpenAPI types no
// longer carry these schemas.
export type VisitSummaryDraft = ContractGenerateDraftResponse['summary']
export type AiConditionDraft = ContractGenerateDraftResponse['conditions'][number]
export type AiProcedureDraft = ContractGenerateDraftResponse['procedures'][number]
export type AiMedicationDraft = ContractGenerateDraftResponse['medications'][number]
export type AiObservationDraft = ContractGenerateDraftResponse['observations'][number]
export type AiAllergyDraft = AiAllergy
export type AiServiceRequestDraft = ContractGenerateDraftResponse['serviceRequests'][number]
export type BillingAddOnDraft = ContractGenerateDraftResponse['billing']['addOnCodes'][number]
export type BillingSuggestionDraft = ContractGenerateDraftResponse['billing']
export type GenerateDraftResponse = ContractGenerateDraftResponse

export type FinalizeMode = 'with-draft' | 'without-draft'

export interface FinalizeScribeResult {
  /** The narrative summary (chiefComplaint/HPI/ROS/Assessment/Plan). Undefined for `without-draft`. */
  summary: VisitSummaryDraft | undefined
  conditions: AiConditionDraft[]
  procedures: AiProcedureDraft[]
  medications: AiMedicationDraft[]
  observations: AiObservationDraft[]
  allergies: AiAllergyDraft[]
  serviceRequests: AiServiceRequestDraft[]
  /** AI-suggested E/M billing code + add-ons + modifiers. Undefined for `without-draft`. */
  billing: BillingSuggestionDraft | undefined
  /** ISO-8601 session start; `null` when absent (buffer gone / without-draft). */
  sessionStartedAt: string | null
  /** Quality-gate verdict — present only when the gate ran and produced one. */
  verification?: GenerateDraftResponse['verification']
  /**
   * PD-1378 — the server refused this run: another generation owns the
   * encounter. Empty by construction; never treat as a failed recovery.
   */
  alreadyGenerating?: true
}

export interface UseFinalizeScribeResult {
  finalize: (
    mode: FinalizeMode,
    patientId?: string,
    doctorObservations?: string,
    /** Interrupted-visit "Finish & generate note": forwards `partial: true` so the AI prompt only extracts what's explicitly present. */
    partial?: boolean,
    /** Recovered session id (interrupted-visit path), used instead of provider state so the flow never races a `setScribeSessionId` dispatch. */
    explicitSessionId?: string,
    /** Fired after the transcript is persisted, before `/generate-draft`; a throw is logged, not propagated. */
    onPersisted?: () => Promise<void> | void,
    /** Drains the live-transcription buffer before `/finalize` (best-effort); omit when there's no live buffer. */
    flushPendingTranscript?: () => Promise<void>,
  ) => Promise<FinalizeScribeResult>
  /** Re-run ONLY `/generate-draft` against the saved transcript — no re-finalize, no clinical writes (a pure, repeatable read). */
  retryDraft: () => Promise<FinalizeScribeResult>
  /**
   * Generate from a persisted `ScribeTranscript` with no live scribe session —
   * the recovery path taken when the doctor re-enters an encounter whose
   * generation failed, long after the Redis buffer expired.
   */
  generateFromTranscript: (
    encounterId: string,
    transcriptDocumentReferenceId: string,
    doctorObservations?: string,
  ) => Promise<FinalizeScribeResult>
  /**
   * PD-1516 — generate the full draft from the provider's AI-Dictate speech,
   * through the same pipeline (chart facts, validation, billing) as a visit.
   */
  generateFromDictation: (
    encounterId: string,
    dictation: string,
    existingNote?: string,
  ) => Promise<FinalizeScribeResult>
  /** Drop the pending retry and reset to idle (the doctor chose to write manually). */
  abandonDraftRetry: () => void
  /** True when a draft failed but the saved transcript is still retryable. */
  draftRetryAvailable: boolean
  isFinalizing: boolean
  isGeneratingDraft: boolean
  /** Latest real generation stage/pct from the event stream; null before the first event (PD-1139). */
  draftProgress: { stage: string; pct: number } | null
}

/** Inputs to `/generate-draft`, retained so a failed draft can be retried. */
type DraftRetryContext = {
  // Captured at finalize time, NOT read live — `leaveSession` (onPersisted) dispatches
  // RESET and nulls `currentEncounterId`, so reading it live here threw "no active encounter".
  encounterId: string
  sessionId: string
  transcriptDocumentReferenceId?: string
  /** PD-1516 — provider AI-Dictate speech as the transcript source. */
  dictation?: string
  /** PD-1516 re-dictation — the current note, updated rather than replaced. */
  existingNote?: string
  doctorObservations?: string
  partial?: boolean
}

/**
 * Narrow a `/generate-draft` response to the shape the note flow consumes.
 * Shared with the recovery path, which reads the same payload back from the
 * stored `ScribeDraft` document rather than from a live response.
 */
export function toFinalizeResult(draftResponse: GenerateDraftResponse): FinalizeScribeResult {
  return {
    summary: draftResponse.summary,
    conditions: draftResponse.conditions ?? [],
    procedures: draftResponse.procedures ?? [],
    medications: draftResponse.medications ?? [],
    observations: draftResponse.observations ?? [],
    allergies: draftResponse.allergies ?? [],
    serviceRequests: draftResponse.serviceRequests ?? [],
    billing: draftResponse.billing,
    sessionStartedAt: draftResponse.sessionStartedAt ?? null,
    verification: draftResponse.verification,
  }
}

function emptyResult(): FinalizeScribeResult {
  return {
    summary: undefined,
    conditions: [],
    procedures: [],
    medications: [],
    observations: [],
    allergies: [],
    serviceRequests: [],
    billing: undefined,
    sessionStartedAt: null,
  }
}

export function useFinalizeScribe(): UseFinalizeScribeResult {
  const client = useClient()
  const { currentEncounterId, scribeSessionId, setScribeSessionId, setScribeState } =
    useCallSession()

  type FinalizeResponse = Awaited<ReturnType<typeof client.scribeFinalizeSession>>

  const finalizeMutation = useMutation({
    mutationFn: async (args: {
      encounterId: string
      sessionId: string
    }): Promise<FinalizeResponse> => {
      if (!args.encounterId) {
        throw new Error('Cannot finalize scribe session: no active encounter')
      }
      if (!args.sessionId) {
        throw new Error('Cannot finalize scribe session: no active session id')
      }
      return client.scribeFinalizeSession({
        encounterId: args.encounterId,
        sessionId: args.sessionId,
      })
    },
  })

  const [draftProgress, setDraftProgress] = useState<{ stage: string; pct: number } | null>(null)

  const generateDraftMutation = useMutation({
    mutationFn: async (args: {
      encounterId: string
      sessionId: string
      transcriptDocumentReferenceId?: string
      dictation?: string
      existingNote?: string
      doctorObservations?: string
      partial?: boolean
    }): Promise<GenerateDraftResponse> => {
      if (!args.encounterId) {
        throw new Error('Cannot generate draft: no active encounter')
      }
      // oRPC streams this endpoint as an event iterator (progress ticks + a
      // terminal result frame) whose 5s keep-alive pings hold the socket active,
      // so the long generation outlives the ALB idle timeout.
      // `transcriptDocumentReferenceId` makes the BE read the durable
      // FHIR Binary, not the hot-reload-volatile in-memory buffer. On failure the
      // client throws an ORPCError whose `.status`/`.message` the scribeErrors
      // classifiers read (same shape they already handled).
      const trimmedObservations = args.doctorObservations?.trim()
      const iter = await client.scribeGenerateDraft({
        encounterId: args.encounterId,
        sessionId: args.sessionId,
        ...(args.transcriptDocumentReferenceId
          ? { transcriptDocumentReferenceId: args.transcriptDocumentReferenceId }
          : null),
        ...(args.dictation ? { dictation: args.dictation } : null),
        ...(args.dictation && args.existingNote ? { existingNote: args.existingNote } : null),
        ...(trimmedObservations ? { doctorObservations: trimmedObservations } : null),
        ...(args.partial ? { partial: true } : null),
      })

      let result: GenerateDraftResponse | undefined
      for await (const ev of iter) {
        if (ev.type === 'progress') {
          setDraftProgress({ stage: ev.stage, pct: ev.pct })
        } else if (ev.type === 'result') {
          result = ev.data
        }
      }
      if (!result) {
        throw new Error('Stream ended without a result event')
      }
      return result
    },
  })

  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
  const [draftRetry, setDraftRetry] = useState<DraftRetryContext | null>(null)

  // On a transient (non-404) failure: keep the session id + transcript handle and throw `ScribeDraftGenerationError`;
  // scribeState stays 'finalizing' so live capture remains detached while the caller offers a retry.
  const runGenerateDraft = useCallback(
    async (ctx: DraftRetryContext): Promise<FinalizeScribeResult> => {
      setIsGeneratingDraft(true)
      setDraftProgress(null)
      let draftResponse: GenerateDraftResponse
      try {
        draftResponse = await generateDraftMutation.mutateAsync(ctx)
      } catch (err) {
        // Only meaningful while the Redis buffer IS the transcript source. Once a
        // durable transcript document is in hand, a 404 is that document failing
        // to read — telling the doctor to "start a new session" would be wrong
        // advice, and swallowing it into an empty result opens a BLANK note.
        if (!ctx.transcriptDocumentReferenceId && !ctx.dictation && isStaleScribeSession404(err)) {
          logWarning(
            'useFinalizeScribe',
            'Stale scribe session detected on /generate-draft — resetting state',
            `sessionId=${ctx.sessionId}`,
          )
          setDraftRetry(null)
          setScribeSessionId(null)
          setScribeState('idle')
          setIsGeneratingDraft(false)
          showNotification({
            color: 'yellow',
            title: 'Scribe session expired',
            message: 'Start a new session to continue.',
          })
          return emptyResult()
        }
        // PD-1378 — another tab (or an earlier click) already has a generation
        // running for this encounter. The retry/error state would be a lie: that
        // run still writes the draft, and `useEncounterScribeState` polls the
        // generation flag and swaps the rail to it once it lands.
        if (isDraftAlreadyGenerating(err)) {
          logWarning(
            'useFinalizeScribe',
            'generate-draft rejected — a generation is already running',
            `sessionId=${ctx.sessionId}`,
          )
          setDraftRetry(null)
          setScribeSessionId(null)
          setScribeState('idle')
          setIsGeneratingDraft(false)
          showNotification({
            color: 'yellow',
            message: 'A draft is already being generated for this visit.',
          })
          return { ...emptyResult(), alreadyGenerating: true }
        }
        logError('useFinalizeScribe', 'generate-draft step failed', err)
        setDraftRetry(ctx)
        setIsGeneratingDraft(false)
        showNotification({
          color: 'red',
          title: 'Draft generation failed',
          message:
            'The transcript was saved. Retry the AI draft, or write the Progress Note manually.',
        })
        throw new ScribeDraftGenerationError()
      }

      setDraftRetry(null)
      setScribeSessionId(null)
      setScribeState('idle')
      setIsGeneratingDraft(false)

      return toFinalizeResult(draftResponse)
    },
    [generateDraftMutation, setScribeSessionId, setScribeState],
  )

  const finalize = useCallback<UseFinalizeScribeResult['finalize']>(
    async (
      mode,
      _patientId,
      doctorObservations,
      partial,
      explicitSessionId,
      onPersisted,
      flushPendingTranscript,
    ) => {
      const effectiveSessionId = explicitSessionId ?? scribeSessionId
      if (!effectiveSessionId) {
        if (mode === 'without-draft') {
          setScribeState('idle')
          return emptyResult()
        }
        // with-draft but no scribe ever started: soft-fall-through to an empty manual note (not a throw — valid-but-unexpected state).
        logWarning(
          'useFinalizeScribe',
          'No active scribe session — generating empty draft',
          'mode=with-draft scribeSessionId=null',
        )
        showNotification({
          color: 'blue',
          message:
            'You did not start the AI scribe. Opening an empty progress note for manual entry.',
        })
        setScribeState('idle')
        return emptyResult()
      }

      // Capture the encounter id NOW, while the call is live — `onPersisted` (leaveSession) dispatches RESET below
      // and nulls `currentEncounterId`, so threading this snapshot keeps off-call generation + retry from reading the wiped value.
      const encounterIdForFlow = currentEncounterId
      if (!encounterIdForFlow) {
        if (mode === 'without-draft') {
          setScribeState('idle')
          return emptyResult()
        }
        logWarning(
          'useFinalizeScribe',
          'No active encounter — generating empty draft',
          'mode=with-draft currentEncounterId=null',
        )
        showNotification({
          color: 'blue',
          message: 'No active encounter. Opening an empty progress note for manual entry.',
        })
        setScribeState('idle')
        return emptyResult()
      }

      // Flip to 'finalizing' BEFORE any network step so `useLiveTranscription` detaches its Zoom LTT listener
      // and no further conversation is captured while the AI generates (the captured id keeps the in-flight POSTs valid).
      setScribeState('finalizing')

      // Frames from a previous generation must not leak into this flow's finalize
      // window — the leave-hint gates on them (a rejoin reuses this hook instance).
      setDraftProgress(null)

      // Flush the last debounce window before the /finalize snapshot — else the effect-cleanup's
      // fire-and-forget flush races the POST and the tail is lost. Best-effort (caught below).
      if (flushPendingTranscript) {
        try {
          await flushPendingTranscript()
        } catch (err) {
          logWarning(
            'useFinalizeScribe',
            'Pending transcript flush before finalize failed — proceeding',
            err instanceof Error ? err.message : String(err),
          )
        }
      }

      // Step 1 — /finalize
      let finalizeResponse: FinalizeResponse
      try {
        finalizeResponse = await finalizeMutation.mutateAsync({
          encounterId: encounterIdForFlow,
          sessionId: effectiveSessionId,
        })
      } catch (err) {
        // Stale-session 404 — middleware restarted or expired the buffer; reset to idle with a gentle prompt, not an error toast.
        if (isStaleScribeSession404(err)) {
          logWarning(
            'useFinalizeScribe',
            'Stale scribe session detected on /finalize — resetting state',
            `sessionId=${effectiveSessionId ?? 'null'}`,
          )
          setScribeSessionId(null)
          setScribeState('idle')
          setIsGeneratingDraft(false)
          showNotification({
            color: 'yellow',
            title: 'Scribe session expired',
            message: 'Start a new session to continue.',
          })
          return emptyResult()
        }
        // Empty transcript (muted mic / LTT never attached) — no draft to make, so open an empty manual note instead of a red retry.
        if (isEmptyTranscriptError(err)) {
          logWarning(
            'useFinalizeScribe',
            'Empty scribe transcript on /finalize — opening empty note',
            `sessionId=${effectiveSessionId ?? 'null'}`,
          )
          setScribeSessionId(null)
          setScribeState('idle')
          setIsGeneratingDraft(false)
          showNotification({
            color: 'blue',
            message:
              'No conversation was captured. Opening an empty progress note for manual entry.',
          })
          return emptyResult()
        }
        // Transient (non-404) failure — the session is still live. Restore 'streaming' (NOT idle): leaving it 'finalizing'
        // would keep `useLiveTranscription` detached and silently kill capture with no recovery; keep `scribeSessionId` (still valid).
        setScribeState('streaming')
        setIsGeneratingDraft(false)
        logError('useFinalizeScribe', 'finalize step failed', err)
        showNotification({
          color: 'red',
          title: 'Scribe error',
          message: 'Could not finalize the scribe session. Please try again.',
        })
        throw err
      }

      const sessionIdForDraft = effectiveSessionId

      if (mode === 'without-draft') {
        setScribeSessionId(null)
        setScribeState('idle')
        showNotification({
          color: 'green',
          message: 'Scribe session finalized.',
        })
        return emptyResult()
      }

      if (!sessionIdForDraft) {
        throw new Error('Cannot generate draft: session id missing after finalize')
      }

      // Transcript is durable now — end the call (leaveSession) before off-call generation; a failure must not abort it.
      if (onPersisted) {
        try {
          await onPersisted()
        } catch (err) {
          logError('useFinalizeScribe', 'onPersisted callback failed', err)
        }
      }

      // Step 2 — /generate-draft, with the FHIR-backed transcript id from /finalize.
      return runGenerateDraft({
        encounterId: encounterIdForFlow,
        sessionId: sessionIdForDraft,
        transcriptDocumentReferenceId: finalizeResponse.transcriptDocumentReferenceId,
        doctorObservations,
        partial,
      })
    },
    [
      currentEncounterId,
      finalizeMutation,
      runGenerateDraft,
      scribeSessionId,
      setScribeSessionId,
      setScribeState,
    ],
  )

  const retryDraft = useCallback(async (): Promise<FinalizeScribeResult> => {
    if (!draftRetry) {
      throw new Error('Cannot retry draft: no pending draft to retry')
    }
    return runGenerateDraft(draftRetry)
  }, [draftRetry, runGenerateDraft])

  const generateFromTranscript = useCallback<UseFinalizeScribeResult['generateFromTranscript']>(
    (encounterId, transcriptDocumentReferenceId, doctorObservations) =>
      runGenerateDraft({
        encounterId,
        // The Redis session expired long before this runs. A non-empty id is
        // still required by the contract, and the handler only reads it for the
        // best-effort session start time once a transcript document is supplied
        // — the transcript itself comes from FHIR, not the buffer.
        sessionId: `recovery-${encounterId}`,
        transcriptDocumentReferenceId,
        doctorObservations,
      }),
    [runGenerateDraft],
  )

  const generateFromDictation = useCallback<UseFinalizeScribeResult['generateFromDictation']>(
    (encounterId, dictation, existingNote) =>
      runGenerateDraft({
        encounterId,
        // No scribe session exists — the contract requires a non-empty id, and
        // the handler skips every session read when `dictation` is present.
        sessionId: `dictate-${encounterId}`,
        dictation,
        existingNote,
      }),
    [runGenerateDraft],
  )

  const abandonDraftRetry = useCallback(() => {
    setDraftRetry(null)
    setScribeSessionId(null)
    setScribeState('idle')
  }, [setScribeSessionId, setScribeState])

  return {
    finalize,
    retryDraft,
    generateFromTranscript,
    generateFromDictation,
    abandonDraftRetry,
    draftRetryAvailable: draftRetry !== null,
    isFinalizing: finalizeMutation.isPending,
    isGeneratingDraft: isGeneratingDraft || generateDraftMutation.isPending,
    draftProgress,
  }
}
